import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import CinematicNav from "@/components/CinematicNav";

gsap.registerPlugin(ScrollTrigger);

/**
 * ARIA È OVUNQUE — Cinematic landing.
 *
 * Aria non è un chatbot. Non ha un volto. È una presenza fatta di particelle
 * che si aggregano, si separano, si dissolvono. 12 scene scroll-driven con
 * particle field Three.js vanilla in background fisso.
 */

const PARTICLE_COUNT = 4200;
const TOTAL_SCENES = 12;

// ============================================================
// Phase orchestrator — ogni numero di phase corrisponde a una
// configurazione del particle field. Le transizioni avvengono
// in modo continuo via damped lerp dei "target" positions.
// ============================================================

function generatePhasePositions(phase, count) {
    const positions = new Float32Array(count * 3);
    const rand = (a, b) => a + Math.random() * (b - a);

    switch (phase) {
        case 0: {
            // SINGOLO PUNTO — tutte concentrate al centro con jitter minimo
            for (let i = 0; i < count; i++) {
                positions[i * 3 + 0] = rand(-0.4, 0.4);
                positions[i * 3 + 1] = rand(-0.4, 0.4);
                positions[i * 3 + 2] = rand(-0.4, 0.4);
            }
            break;
        }
        case 1: {
            // SFERA — esplosione organica in sfera diffusa
            for (let i = 0; i < count; i++) {
                const r = rand(10, 22);
                const theta = rand(0, Math.PI * 2);
                const phi = Math.acos(rand(-1, 1));
                positions[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
                positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
                positions[i * 3 + 2] = r * Math.cos(phi);
            }
            break;
        }
        case 2: {
            // OVUNQUE — distribuzione box ampia, drift lento
            for (let i = 0; i < count; i++) {
                positions[i * 3 + 0] = rand(-40, 40);
                positions[i * 3 + 1] = rand(-22, 22);
                positions[i * 3 + 2] = rand(-25, 5);
            }
            break;
        }
        case 3: {
            // ISOLATI — cluster separati, simulano "siti soli"
            const clusterCount = 12;
            const centers = [];
            for (let c = 0; c < clusterCount; c++) {
                centers.push([rand(-30, 30), rand(-18, 18), rand(-20, 0)]);
            }
            for (let i = 0; i < count; i++) {
                const c = centers[i % clusterCount];
                positions[i * 3 + 0] = c[0] + rand(-2, 2);
                positions[i * 3 + 1] = c[1] + rand(-2, 2);
                positions[i * 3 + 2] = c[2] + rand(-2, 2);
            }
            break;
        }
        case 4: {
            // NETWORK — cluster + linee (la connessione è gestita dalle LineSegments)
            const clusterCount = 14;
            const centers = [];
            for (let c = 0; c < clusterCount; c++) {
                centers.push([rand(-26, 26), rand(-16, 16), rand(-18, 2)]);
            }
            for (let i = 0; i < count; i++) {
                const c = centers[i % clusterCount];
                positions[i * 3 + 0] = c[0] + rand(-1.5, 1.5);
                positions[i * 3 + 1] = c[1] + rand(-1.5, 1.5);
                positions[i * 3 + 2] = c[2] + rand(-1.5, 1.5);
            }
            break;
        }
        case 5: {
            // HOST-FIRST — due cluster verticali con un flow tra loro
            for (let i = 0; i < count; i++) {
                const t = i / count;
                if (t < 0.45) {
                    // cluster sinistro = sito ospitante
                    positions[i * 3 + 0] = -14 + rand(-3, 3);
                    positions[i * 3 + 1] = rand(-7, 7);
                    positions[i * 3 + 2] = rand(-10, 0);
                } else if (t < 0.55) {
                    // ponte centrale
                    positions[i * 3 + 0] = rand(-12, 12);
                    positions[i * 3 + 1] = rand(-1.5, 1.5);
                    positions[i * 3 + 2] = rand(-8, 0);
                } else {
                    // cluster destro = world / network
                    positions[i * 3 + 0] = 16 + rand(-4, 4);
                    positions[i * 3 + 1] = rand(-10, 10);
                    positions[i * 3 + 2] = rand(-12, 0);
                }
            }
            break;
        }
        case 6: {
            // NETWORK CRESCE — costellazione densa interconnessa
            for (let i = 0; i < count; i++) {
                const r = rand(6, 24);
                const theta = rand(0, Math.PI * 2);
                positions[i * 3 + 0] = r * Math.cos(theta);
                positions[i * 3 + 1] = rand(-12, 12);
                positions[i * 3 + 2] = r * Math.sin(theta) - 6;
            }
            break;
        }
        case 7: {
            // VOLTO QUASI — particelle distribuite secondo profilo di volto stilizzato
            // ellisse principale (testa) + densità accentata su zone occhi
            for (let i = 0; i < count; i++) {
                const t = Math.random();
                if (t < 0.62) {
                    // contorno testa: ellisse
                    const a = rand(0, Math.PI * 2);
                    const r = rand(7, 9);
                    positions[i * 3 + 0] = Math.cos(a) * r * 0.78 + rand(-0.6, 0.6);
                    positions[i * 3 + 1] = Math.sin(a) * r * 1.05 + rand(-0.6, 0.6);
                    positions[i * 3 + 2] = rand(-6, 2);
                } else if (t < 0.76) {
                    // occhio sinistro
                    positions[i * 3 + 0] = -2.6 + rand(-0.8, 0.8);
                    positions[i * 3 + 1] = 1.6 + rand(-0.4, 0.4);
                    positions[i * 3 + 2] = rand(-5, 1);
                } else if (t < 0.9) {
                    // occhio destro
                    positions[i * 3 + 0] = 2.6 + rand(-0.8, 0.8);
                    positions[i * 3 + 1] = 1.6 + rand(-0.4, 0.4);
                    positions[i * 3 + 2] = rand(-5, 1);
                } else {
                    // rumore: particelle che lo fanno mai completare
                    positions[i * 3 + 0] = rand(-12, 12);
                    positions[i * 3 + 1] = rand(-12, 12);
                    positions[i * 3 + 2] = rand(-6, 2);
                }
            }
            break;
        }
        case 8: {
            // FLOW — colonna verticale, particelle che scorrono dall'alto in basso
            for (let i = 0; i < count; i++) {
                positions[i * 3 + 0] = rand(-3, 3);
                positions[i * 3 + 1] = rand(-15, 15);
                positions[i * 3 + 2] = rand(-8, 2);
            }
            break;
        }
        case 9: {
            // INSTALL — 3 nuclei orizzontali (steps 1-2-3)
            for (let i = 0; i < count; i++) {
                const slot = i % 3;
                const cx = (slot - 1) * 14;
                positions[i * 3 + 0] = cx + rand(-3, 3);
                positions[i * 3 + 1] = rand(-4, 4);
                positions[i * 3 + 2] = rand(-8, 0);
            }
            break;
        }
        case 10: {
            // SOCIAL PROOF — formazione disco orizzontale, "presenza estesa"
            for (let i = 0; i < count; i++) {
                const a = rand(0, Math.PI * 2);
                const r = rand(0, 18);
                positions[i * 3 + 0] = Math.cos(a) * r;
                positions[i * 3 + 1] = rand(-0.6, 0.6) + Math.sin(i * 0.1) * 1.5;
                positions[i * 3 + 2] = Math.sin(a) * r - 4;
            }
            break;
        }
        case 11: {
            // FINAL — quasi-statiche, rallentano, dissolve cinematic
            for (let i = 0; i < count; i++) {
                const t = i / count;
                const r = rand(4, 16) * (1 - t * 0.3);
                const theta = rand(0, Math.PI * 2);
                positions[i * 3 + 0] = r * Math.cos(theta);
                positions[i * 3 + 1] = r * Math.sin(theta) * 0.55;
                positions[i * 3 + 2] = rand(-12, 0);
            }
            break;
        }
        default:
            for (let i = 0; i < count; i++) {
                positions[i * 3 + 0] = rand(-5, 5);
                positions[i * 3 + 1] = rand(-5, 5);
                positions[i * 3 + 2] = rand(-5, 5);
            }
    }
    return positions;
}

// Configurazione visiva per phase (colori + scale + flag)
const PHASE_CONFIG = [
    { color: 0xFFFFFF, secondary: 0x6BB4FF, size: 0.06, lines: false,  rotSpeed: 0.001, label: "Origine" },
    { color: 0xCFE3FF, secondary: 0x4F8BFF, size: 0.045, lines: false, rotSpeed: 0.0008, label: "Espansione" },
    { color: 0xCFE3FF, secondary: 0x9EC3FF, size: 0.04,  lines: false, rotSpeed: 0.0005, label: "Ovunque" },
    { color: 0xFFFFFF, secondary: 0x5B6B92, size: 0.04,  lines: false, rotSpeed: 0.0006, label: "Isolati" },
    { color: 0xC8DDFF, secondary: 0x4F8BFF, size: 0.05,  lines: true,  rotSpeed: 0.0009, label: "Rete" },
    { color: 0xA5F3FC, secondary: 0x4F8BFF, size: 0.05,  lines: true,  rotSpeed: 0.0008, label: "Host First" },
    { color: 0x9EC3FF, secondary: 0xFBBF24, size: 0.05,  lines: true,  rotSpeed: 0.0012, label: "Crescita" },
    { color: 0xFFE5C4, secondary: 0xFBBF24, size: 0.045, lines: false, rotSpeed: 0.0003, label: "Volto" },
    { color: 0xCFE3FF, secondary: 0x4F8BFF, size: 0.045, lines: false, rotSpeed: 0.0008, label: "Flusso" },
    { color: 0xC4FFE5, secondary: 0x4F8BFF, size: 0.05,  lines: false, rotSpeed: 0.0006, label: "Installa" },
    { color: 0x9EC3FF, secondary: 0x4F8BFF, size: 0.06,  lines: true,  rotSpeed: 0.0009, label: "Proof" },
    { color: 0xFFFFFF, secondary: 0xCFE3FF, size: 0.05,  lines: false, rotSpeed: 0.0002, label: "Finale" },
];

// ============================================================
// PARTICLE FIELD — vanilla Three.js singleton montato sull'app
// ============================================================

function useParticleField(canvasRef, phaseRef) {
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const scene = new THREE.Scene();
        scene.fog = new THREE.FogExp2(0x05050F, 0.018);

        const camera = new THREE.PerspectiveCamera(
            55,
            window.innerWidth / window.innerHeight,
            0.1,
            300
        );
        camera.position.set(0, 0, 32);

        const renderer = new THREE.WebGLRenderer({
            canvas,
            alpha: true,
            antialias: true,
            powerPreference: "high-performance",
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.8));
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setClearColor(0x05050F, 1);

        // Buffer attributes
        const positions = new Float32Array(PARTICLE_COUNT * 3);
        const targetPositions = new Float32Array(PARTICLE_COUNT * 3);
        const velocities = new Float32Array(PARTICLE_COUNT * 3);
        const colorsA = new Float32Array(PARTICLE_COUNT * 3);

        // start positions = phase 0
        const initial = generatePhasePositions(0, PARTICLE_COUNT);
        positions.set(initial);
        targetPositions.set(initial);

        // setup colors mix primary/secondary
        function applyPhaseColors(phaseIdx) {
            const cfg = PHASE_CONFIG[phaseIdx] || PHASE_CONFIG[0];
            const primary = new THREE.Color(cfg.color);
            const secondary = new THREE.Color(cfg.secondary);
            for (let i = 0; i < PARTICLE_COUNT; i++) {
                const t = Math.random();
                const c = t > 0.85 ? secondary : primary;
                colorsA[i * 3 + 0] = c.r;
                colorsA[i * 3 + 1] = c.g;
                colorsA[i * 3 + 2] = c.b;
            }
            geometry.attributes.color.needsUpdate = true;
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute("color", new THREE.BufferAttribute(colorsA, 3));

        // Custom shader-like via PointsMaterial
        const material = new THREE.PointsMaterial({
            size: 0.06,
            transparent: true,
            opacity: 0.95,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            vertexColors: true,
            sizeAttenuation: true,
        });

        const points = new THREE.Points(geometry, material);
        scene.add(points);

        // === LINES (network connections) ===
        const MAX_LINES = 160;
        const linePositions = new Float32Array(MAX_LINES * 2 * 3);
        const lineGeometry = new THREE.BufferGeometry();
        lineGeometry.setAttribute("position", new THREE.BufferAttribute(linePositions, 3));
        const lineMaterial = new THREE.LineBasicMaterial({
            color: 0x4F8BFF,
            transparent: true,
            opacity: 0.0,
            blending: THREE.AdditiveBlending,
        });
        const linesMesh = new THREE.LineSegments(lineGeometry, lineMaterial);
        scene.add(linesMesh);

        applyPhaseColors(0);

        // Mouse parallax (disabilitato su touch device per evitare drift)
        const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
        const isTouchDevice = typeof window !== "undefined" && (("ontouchstart" in window) || navigator.maxTouchPoints > 0);
        const onMouseMove = (e) => {
            if (isTouchDevice) return;
            mouse.tx = (e.clientX / window.innerWidth - 0.5) * 2;
            mouse.ty = -(e.clientY / window.innerHeight - 0.5) * 2;
        };
        window.addEventListener("mousemove", onMouseMove);

        const onResize = () => {
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.position.z = window.innerWidth < 768 ? 38 : 32;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        };
        window.addEventListener("resize", onResize);
        onResize();

        // Phase change watcher
        let currentPhase = 0;
        let lastPhaseChangeAt = performance.now();
        function switchTo(newPhase) {
            currentPhase = newPhase;
            const np = generatePhasePositions(newPhase, PARTICLE_COUNT);
            targetPositions.set(np);
            applyPhaseColors(newPhase);
            material.size = PHASE_CONFIG[newPhase].size;
            // toggle linee
            gsap.to(lineMaterial, {
                opacity: PHASE_CONFIG[newPhase].lines ? 0.35 : 0,
                duration: 1.2,
                ease: "power2.out",
            });
            lastPhaseChangeAt = performance.now();
        }

        // Init phase
        switchTo(0);

        // Animation loop
        const clock = new THREE.Clock();
        let rafId = 0;

        function animate() {
            rafId = requestAnimationFrame(animate);
            const t = clock.getElapsedTime();
            const dt = clock.getDelta();

            // sync to externally-set phase
            const requested = phaseRef.current;
            if (requested !== currentPhase) switchTo(requested);

            const cfg = PHASE_CONFIG[currentPhase];
            // smooth mouse parallax
            mouse.x += (mouse.tx - mouse.x) * 0.06;
            mouse.y += (mouse.ty - mouse.y) * 0.06;
            camera.position.x += (mouse.x * 3 - camera.position.x) * 0.05;
            camera.position.y += (mouse.y * 2 - camera.position.y) * 0.05;
            camera.lookAt(0, 0, 0);

            // global rotation
            points.rotation.y += cfg.rotSpeed;
            points.rotation.x = Math.sin(t * 0.18) * 0.06;
            linesMesh.rotation.copy(points.rotation);

            // damped lerp positions → target + organic noise
            const pos = geometry.attributes.position.array;
            const damp = currentPhase === 7 ? 0.018 : currentPhase === 11 ? 0.012 : 0.04;
            // Flow phase (8): particelle scorrono dall'alto in basso
            const flowing = currentPhase === 8;
            const noiseAmp = currentPhase === 7 ? 0.12 : currentPhase === 11 ? 0.04 : 0.06;
            for (let i = 0; i < PARTICLE_COUNT; i++) {
                const i3 = i * 3;
                // micro noise
                const nx = Math.sin(t * 0.4 + i * 0.13) * noiseAmp;
                const ny = Math.cos(t * 0.5 + i * 0.17) * noiseAmp;
                const nz = Math.sin(t * 0.3 + i * 0.07) * noiseAmp;
                pos[i3 + 0] += (targetPositions[i3 + 0] + nx - pos[i3 + 0]) * damp;
                pos[i3 + 1] += (targetPositions[i3 + 1] + ny - pos[i3 + 1]) * damp;
                pos[i3 + 2] += (targetPositions[i3 + 2] + nz - pos[i3 + 2]) * damp;

                // Flow: drift in basso
                if (flowing) {
                    pos[i3 + 1] -= 0.08;
                    if (pos[i3 + 1] < -16) {
                        pos[i3 + 1] = 16 + Math.random() * 4;
                        pos[i3 + 0] = (Math.random() - 0.5) * 6;
                    }
                }
            }
            geometry.attributes.position.needsUpdate = true;

            // Lines: solo per phase con lines=true
            if (cfg.lines) {
                let lineIdx = 0;
                // Pick MAX_LINES random pair within distance threshold
                for (let l = 0; l < MAX_LINES; l++) {
                    const a = (Math.floor(Math.random() * PARTICLE_COUNT)) * 3;
                    const b = (Math.floor(Math.random() * PARTICLE_COUNT)) * 3;
                    const dx = pos[a] - pos[b];
                    const dy = pos[a + 1] - pos[b + 1];
                    const dz = pos[a + 2] - pos[b + 2];
                    const d2 = dx * dx + dy * dy + dz * dz;
                    if (d2 < 25) {
                        linePositions[lineIdx++] = pos[a];
                        linePositions[lineIdx++] = pos[a + 1];
                        linePositions[lineIdx++] = pos[a + 2];
                        linePositions[lineIdx++] = pos[b];
                        linePositions[lineIdx++] = pos[b + 1];
                        linePositions[lineIdx++] = pos[b + 2];
                    }
                }
                // Riempi il resto con segmenti collassati (invisibili)
                for (; lineIdx < MAX_LINES * 6; lineIdx++) linePositions[lineIdx] = 0;
                lineGeometry.attributes.position.needsUpdate = true;
            }

            renderer.render(scene, camera);
        }
        animate();

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("resize", onResize);
            renderer.dispose();
            geometry.dispose();
            lineGeometry.dispose();
            material.dispose();
            lineMaterial.dispose();
        };
    }, [canvasRef, phaseRef]);
}

// ============================================================
// SCROLL ORCHESTRATOR
// ============================================================

function useSceneScroll(phaseRef, setActiveScene) {
    useEffect(() => {
        const sections = document.querySelectorAll("[data-aria-scene]");
        const triggers = [];
        sections.forEach((sec) => {
            const idx = parseInt(sec.getAttribute("data-aria-scene"), 10);
            const tr = ScrollTrigger.create({
                trigger: sec,
                start: "top 60%",
                end: "bottom 40%",
                onEnter: () => { phaseRef.current = idx; setActiveScene(idx); },
                onEnterBack: () => { phaseRef.current = idx; setActiveScene(idx); },
            });
            triggers.push(tr);
        });
        return () => triggers.forEach((t) => t.kill());
    }, [phaseRef, setActiveScene]);
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function AriaIsEverywhere() {
    const canvasRef = useRef(null);
    const phaseRef = useRef(0);
    const [activeScene, setActiveScene] = useState(0);

    useEffect(() => { document.title = "Aria è ovunque — TotalKlick"; }, []);

    useParticleField(canvasRef, phaseRef);
    useSceneScroll(phaseRef, setActiveScene);

    return (
        <div className="relative" style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }} data-testid="aria-is-everywhere">
            {/* Canvas Three.js fissato in background */}
            <canvas ref={canvasRef} className="fixed inset-0 w-screen h-screen pointer-events-none" style={{ zIndex: 0 }} />

            {/* TopNav cinematic unificata (fissa, condivisa con Hub) */}
            <CinematicNav />

            {/* Scene indicator overlay (solo barre — niente etichette) */}
            <div className="fixed left-6 top-1/2 -translate-y-1/2 z-20 hidden md:block">
                <SceneIndicator active={activeScene} />
            </div>

            {/* SCENES — relative z-10 sopra al canvas */}
            <main className="relative z-10">
                {/* === SCENA 1 — Origine === */}
                <Scene idx={0} testid="scene-01">
                    <div className="text-center">
                        <Eyebrow>scena · 01</Eyebrow>
                        <BigLine>
                            C&apos;è qualcosa<br />che <em className="not-italic" style={accent}>sta cambiando</em>.
                        </BigLine>
                        <ScrollHint />
                    </div>
                </Scene>

                {/* === SCENA 2 — Le particelle === */}
                <Scene idx={1} testid="scene-02">
                    <div className="text-center max-w-3xl mx-auto space-y-10">
                        <StaggerLine>Non è un&apos;app.</StaggerLine>
                        <StaggerLine>Non è un chatbot.</StaggerLine>
                        <StaggerLine accent>È una presenza.</StaggerLine>
                    </div>
                </Scene>

                {/* === SCENA 3 — Aria è ovunque === */}
                <Scene idx={2} testid="scene-03" tall>
                    <div className="text-center w-full">
                        <Eyebrow>presente in</Eyebrow>
                        <h1 style={{
                            fontFamily: "Geist, sans-serif",
                            fontWeight: 200,
                            fontSize: "clamp(3rem, 12vw, 10rem)",
                            letterSpacing: "-0.06em",
                            lineHeight: 0.9,
                            background: "linear-gradient(180deg, #FFFFFF 0%, #9EC3FF 65%, rgba(158,195,255,0.3) 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                        }} data-testid="hero-everywhere">
                            ARIA È<br />OVUNQUE.
                        </h1>
                        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-2.5 max-w-3xl mx-auto opacity-70">
                            {[
                                "aziende", "persone", "siti", "negozi",
                                "ristoranti", "hotel", "palestre", "professionisti",
                            ].map((w) => (
                                <span key={w} className="text-[11px] uppercase tracking-[0.2em] py-2 border-y border-white/10" style={{ fontFamily: "JetBrains Mono, monospace" }}>{w}</span>
                            ))}
                        </div>
                    </div>
                </Scene>

                {/* === SCENA 4 — Il problema === */}
                <Scene idx={3} testid="scene-04">
                    <div className="text-center max-w-3xl mx-auto space-y-10">
                        <Eyebrow>il problema</Eyebrow>
                        <StaggerLine>Oggi ogni sito vive da solo.</StaggerLine>
                        <StaggerLine>Ogni conversazione ricomincia da zero.</StaggerLine>
                        <StaggerLine accent dim>Ogni opportunità si perde.</StaggerLine>
                    </div>
                </Scene>

                {/* === SCENA 5 — La nascita === */}
                <Scene idx={4} testid="scene-05">
                    <div className="text-center max-w-3xl mx-auto space-y-12">
                        <Eyebrow>la nascita</Eyebrow>
                        <div className="space-y-7">
                            <StaggerLine>Aria <em className="not-italic" style={accent}>ascolta</em>.</StaggerLine>
                            <StaggerLine>Aria <em className="not-italic" style={accent}>comprende</em>.</StaggerLine>
                            <StaggerLine>Aria <em className="not-italic" style={accent}>connette</em>.</StaggerLine>
                        </div>
                    </div>
                </Scene>

                {/* === SCENA 6 — Host First === */}
                <Scene idx={5} testid="scene-06" tall>
                    <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <Eyebrow>regola d&apos;oro · host first</Eyebrow>
                            <h2 className="text-white mt-3 mb-6" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(2.4rem, 5vw, 4.2rem)", letterSpacing: "-0.04em", lineHeight: 1 }}>
                                Prima aiuta <span style={accent}>te</span>.<br />Poi il mondo<br />attorno a te.
                            </h2>
                            <p className="text-white/55 text-[15px] leading-relaxed max-w-md" style={{ fontFamily: "Outfit, sans-serif" }}>
                                L&apos;istanza Aria del tuo sito ti è fedele al 100%. Mai concorrenti diretti.
                                Mai partner esterni prima che il tuo obiettivo sia raggiunto.
                            </p>
                        </div>
                        <div className="space-y-2">
                            {[
                                { l: "utente", c: "rgba(255,255,255,0.6)" },
                                { l: "il tuo sito", c: "#4F8BFF", strong: true },
                                { l: "lead raccolto", c: "rgba(255,255,255,0.7)" },
                                { l: "obiettivo raggiunto", c: "#86EFAC", strong: true },
                                { l: "→ network", c: "rgba(255,255,255,0.4)", subtle: true },
                            ].map((s, i) => (
                                <div key={i} className="flex items-center gap-3 px-4 py-3.5 rounded-xl transition" style={{
                                    background: s.strong ? `${s.c}12` : "rgba(15,18,36,0.5)",
                                    border: `1px solid ${s.strong ? `${s.c}55` : "rgba(255,255,255,0.06)"}`,
                                }}>
                                    <span className="text-[10px] w-6 inline-block text-white/40 tracking-[0.2em]" style={{ fontFamily: "JetBrains Mono, monospace" }}>{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-[14.5px]" style={{ color: s.c, fontFamily: "Geist, sans-serif", fontWeight: s.strong ? 500 : 300, opacity: s.subtle ? 0.6 : 1 }}>{s.l}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Scene>

                {/* === SCENA 7 — Network === */}
                <Scene idx={6} testid="scene-07">
                    <div className="text-center max-w-3xl mx-auto space-y-8">
                        <Eyebrow>la rete cresce</Eyebrow>
                        <BigLine>
                            Ogni installazione<br />rende Aria<br /><em className="not-italic" style={accent}>più intelligente</em>.
                        </BigLine>
                        <p className="text-white/50 text-[14px] max-w-md mx-auto" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Ogni nuovo sito = un nodo della rete. Ogni nodo aumenta le possibilità di match.
                        </p>
                    </div>
                </Scene>

                {/* === SCENA 8 — Il volto === */}
                <Scene idx={7} testid="scene-08" tall>
                    <div className="text-center w-full max-w-2xl mx-auto">
                        <Eyebrow>presenza</Eyebrow>
                        <div className="my-32 md:my-40">
                            <StaggerLine>Non la vedi.</StaggerLine>
                            <div className="h-10" />
                            <StaggerLine accent>Ma c&apos;è.</StaggerLine>
                        </div>
                    </div>
                </Scene>

                {/* === SCENA 9 — Cosa fa === */}
                <Scene idx={8} testid="scene-09">
                    <div className="text-center max-w-4xl mx-auto px-6">
                        <Eyebrow>il flusso</Eyebrow>
                        <h2 className="text-white mt-3 mb-12" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(2rem, 4.5vw, 3.6rem)", letterSpacing: "-0.04em" }}>
                            Internet collega <span className="text-white/35">pagine</span>.<br />
                            Aria collega <span style={accent}>bisogni</span>.
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5">
                            {[
                                "lead",
                                "comprensione",
                                "future needs",
                                "network match",
                                "opportunità",
                            ].map((s, i) => (
                                <div key={s} className="rounded-xl py-4 px-3" style={{
                                    background: "rgba(15,18,36,0.5)",
                                    border: "1px solid rgba(255,255,255,0.06)",
                                    backdropFilter: "blur(8px)",
                                }}>
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/35 mb-1.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>{String(i + 1).padStart(2, "0")}</div>
                                    <div className="text-[14px] text-white capitalize" style={{ fontFamily: "Geist, sans-serif", fontWeight: 400 }}>{s}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Scene>

                {/* === SCENA 10 — Installazione === */}
                <Scene idx={9} testid="scene-10">
                    <div className="text-center max-w-4xl mx-auto px-6">
                        <Eyebrow>installazione · 3 step</Eyebrow>
                        <h2 className="text-white mt-3 mb-10" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(2rem, 4.5vw, 3.4rem)", letterSpacing: "-0.04em" }}>
                            Bastano <span style={accent}>5 minuti</span>.
                        </h2>
                        <div className="grid md:grid-cols-3 gap-3">
                            {[
                                { n: "01", t: "Installa Aria", d: "Uno snippet JS, niente plugin, niente codice da scrivere." },
                                { n: "02", t: "Raccontale chi sei", d: "Wizard 6 step: profilo, conoscenza, obiettivo, geo." },
                                { n: "03", t: "Lasciala lavorare", d: "Lead arricchiti, host-first sempre, dashboard live." },
                            ].map((s) => (
                                <div key={s.n} className="rounded-2xl p-6 text-left" style={{
                                    background: "rgba(15,18,36,0.55)",
                                    border: "1px solid rgba(255,255,255,0.07)",
                                    backdropFilter: "blur(14px)",
                                }}>
                                    <div className="text-[10px] tracking-[0.25em] uppercase mb-3" style={{ fontFamily: "JetBrains Mono, monospace", color: "#4F8BFF" }}>step · {s.n}</div>
                                    <div className="text-white text-[18px] mb-2" style={{ fontFamily: "Geist, sans-serif", fontWeight: 400 }}>{s.t}</div>
                                    <p className="text-white/55 text-[13px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>{s.d}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </Scene>

                {/* === SCENA 11 — Social proof === */}
                <Scene idx={10} testid="scene-11">
                    <div className="text-center max-w-4xl mx-auto px-6">
                        <Eyebrow>la rete oggi</Eyebrow>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                            {[
                                { v: "∞", l: "attività collegate", sub: "in crescita ogni giorno" },
                                { v: "100%", l: "host-first policy", sub: "regola inderogabile" },
                                { v: "0€", l: "costo installazione", sub: "gratuita per sempre" },
                                { v: "5'", l: "tempo setup", sub: "wizard guidato" },
                            ].map((s, i) => (
                                <div key={i} className="rounded-2xl p-5" style={{
                                    background: "rgba(15,18,36,0.6)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    backdropFilter: "blur(12px)",
                                }}>
                                    <div className="text-[clamp(2rem,4vw,3rem)] leading-none" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, color: "#9EC3FF", letterSpacing: "-0.03em" }}>{s.v}</div>
                                    <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/60 mt-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>{s.l}</div>
                                    <div className="text-[11px] text-white/35 mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{s.sub}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Scene>

                {/* === SCENA 12 — Chiusura + CTA === */}
                <Scene idx={11} testid="scene-12" tall>
                    <div className="text-center max-w-3xl mx-auto">
                        <Eyebrow>chiusura</Eyebrow>
                        <h2 className="text-white mt-4 mb-3" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(2.4rem, 5.5vw, 4.6rem)", letterSpacing: "-0.04em", lineHeight: 1.02 }}>
                            Ogni sito ha una <span style={accent}>voce</span>.
                        </h2>
                        <h3 className="text-white/70 mb-14" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(1.4rem, 3vw, 2.2rem)", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
                            Aria le permette<br />di essere ascoltate.
                        </h3>

                        {/* CTA */}
                        <div className="flex flex-col items-center gap-4">
                            <div className="flex flex-col sm:flex-row items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => { try { window.dispatchEvent(new Event("open-aria")); } catch (e) {} }}
                                    className="group inline-flex items-center gap-3 px-7 py-4 rounded-full text-white text-[15px] transition hover:scale-[1.03]"
                                    style={{
                                        background: "linear-gradient(135deg, #4F8BFF 0%, #2B5AFF 100%)",
                                        fontFamily: "Geist, sans-serif",
                                        fontWeight: 500,
                                        boxShadow: "0 25px 60px -12px rgba(79,139,255,0.55), 0 0 0 1px rgba(255,255,255,0.08) inset",
                                    }}
                                    data-testid="cta-talk-with-aria"
                                >
                                    <Sparkles size={15} />
                                    Parla con Aria
                                    <ArrowRight size={15} className="group-hover:translate-x-1 transition" />
                                </button>
                                <Link
                                    to="/signup"
                                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-[14px] text-white/85 hover:text-white transition hover:scale-[1.02]"
                                    style={{
                                        background: "rgba(255,255,255,0.04)",
                                        border: "1px solid rgba(255,255,255,0.12)",
                                        fontFamily: "Geist, sans-serif",
                                        backdropFilter: "blur(10px)",
                                    }}
                                    data-testid="cta-enter-ecosystem"
                                >
                                    Entra nell&apos;Ecosistema
                                </Link>
                            </div>
                            <div className="text-[11px] text-white/40 tracking-[0.18em] uppercase mt-3" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                5 minuti · nessun costo · nessuna carta di credito
                            </div>
                        </div>
                    </div>
                </Scene>

                {/* Footer cinematic */}
                <footer className="relative z-10 py-16 text-center">
                    <div className="text-white/30 text-[10.5px] tracking-[0.4em] uppercase" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        aria · è · ovunque
                    </div>
                    <Link to="/" className="inline-block mt-4 text-white/40 hover:text-white text-[11.5px] transition" style={{ fontFamily: "Outfit, sans-serif" }}>
                        ← torna a totalklick
                    </Link>
                </footer>
            </main>

            <style>{baseCSS}</style>
        </div>
    );
}

// ============================================================
// HELPERS
// ============================================================

const accent = { color: "#9EC3FF", fontStyle: "normal" };

function Scene({ idx, testid, tall, children }) {
    return (
        <section
            data-aria-scene={idx}
            data-testid={testid}
            className={`relative flex items-center justify-center px-6 ${tall ? "min-h-screen py-20" : "min-h-[72vh] py-14"}`}
        >
            {children}
        </section>
    );
}

function Eyebrow() {
    // Etichette tecniche rimosse per richiesta UX (l'utente vive la storia, non vede la struttura)
    return null;
}

function BigLine({ children }) {
    return (
        <h2 style={{
            fontFamily: "Geist, sans-serif",
            fontWeight: 200,
            fontSize: "clamp(2.2rem, 5.2vw, 4.8rem)",
            letterSpacing: "-0.04em",
            lineHeight: 1.05,
            color: "rgba(255,255,255,0.95)",
        }}>{children}</h2>
    );
}

function StaggerLine({ children, accent: isAccent, dim }) {
    return (
        <div
            className="block"
            style={{
                fontFamily: "Geist, sans-serif",
                fontWeight: isAccent ? 400 : 200,
                fontSize: "clamp(1.6rem, 4vw, 3rem)",
                letterSpacing: "-0.02em",
                lineHeight: 1.15,
                color: dim ? "rgba(255,255,255,0.4)" : isAccent ? "#9EC3FF" : "rgba(255,255,255,0.9)",
            }}
        >
            {children}
        </div>
    );
}

function ScrollHint() {
    return (
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/30">
            <div className="text-[9.5px] uppercase tracking-[0.32em]" style={{ fontFamily: "JetBrains Mono, monospace" }}>scorri</div>
            <div className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent animate-pulse" />
        </div>
    );
}

function SceneIndicator({ active }) {
    return (
        <div className="flex flex-col items-center gap-1.5" data-testid="scene-indicator">
            {Array.from({ length: TOTAL_SCENES }).map((_, i) => (
                <span key={i} className="transition-all duration-700" style={{
                    width: "1.5px",
                    height: i === active ? "18px" : "6px",
                    background: i === active ? "#9EC3FF" : "rgba(255,255,255,0.15)",
                }} />
            ))}
        </div>
    );
}

const baseCSS = `
html,body{background:#05050F;}
::selection{background:rgba(79,139,255,0.4);color:#fff;}
`;
